import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { CheckCircle, XCircle, Star, Trash2, FileText, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { articleService, authService } from '../services/api';

function AdminDashboard() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await articleService.getForManagement();
      setArticles(response.data);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredArticles = () => {
    let filtered;
    if (filter === 'all') filtered = articles;
    else if (filter === 'featured') filtered = articles.filter(a => a.isFeatured);
    else filtered = articles.filter(a => (a.status || '').trim().toLowerCase() === filter.trim().toLowerCase());
    return [...filtered].sort((a, b) => new Date(b.submissionDate) - new Date(a.submissionDate));
  };

  const handleStatusChange = async (articleId, newStatus) => {
    try {
      await articleService.updateStatus(articleId, newStatus);
      await fetchArticles();
    } catch (err) {
      console.error('Error updating article status:', err);
      alert('خطا در تغییر وضعیت مقاله');
    }
  };

  const handleToggleFeatured = async (articleId, isFeatured) => {
    try {
      await articleService.updateFeatured(articleId, isFeatured);
      await fetchArticles();
    } catch (err) {
      console.error('Error updating featured:', err);
      alert('خطا در تنظیم برگزیده');
    }
  };

  const handleDelete = async (articleId) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      try {
        await articleService.delete(articleId);
        await fetchArticles();
      } catch (err) {
        console.error('Error deleting article:', err);
        alert('خطا در حذف مقاله');
      }
    }
  };

  const filteredArticles = getFilteredArticles();

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت مقالات</h1>
              <p className="text-xl text-blue-100">مشاهده، تایید و مدیریت مقالات بارگذاری شده</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Filter Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { value: 'all', label: 'همه', color: 'gray', icon: FileText, ring: 'ring-gray-200', border: 'border-gray-500', bg: 'bg-gray-50', text: 'text-gray-700' },
            { value: 'Draft', label: 'پیش‌نویس', color: 'yellow', icon: FileText, ring: 'ring-yellow-200', border: 'border-yellow-500', bg: 'bg-yellow-50', text: 'text-yellow-700' },
            { value: 'Under Review', label: 'در حال بررسی', color: 'blue', icon: CheckCircle, ring: 'ring-blue-200', border: 'border-blue-500', bg: 'bg-blue-50', text: 'text-blue-700' },
            { value: 'Published', label: 'منتشر شده', color: 'green', icon: CheckCircle, ring: 'ring-green-200', border: 'border-green-500', bg: 'bg-green-50', text: 'text-green-700' },
            { value: 'Rejected', label: 'رد شده', color: 'red', icon: XCircle, ring: 'ring-red-200', border: 'border-red-500', bg: 'bg-red-50', text: 'text-red-700' },
            { value: 'featured', label: 'برگزیده', color: 'purple', icon: Star, ring: 'ring-purple-200', border: 'border-purple-500', bg: 'bg-purple-50', text: 'text-purple-700' },
          ].map(card => {
            const Icon = card.icon;
            const count = card.value === 'all'
              ? articles.length
              : card.value === 'featured'
                ? articles.filter(a => a.isFeatured).length
                : articles.filter(a => (a.status || '').trim().toLowerCase() === card.value.trim().toLowerCase()).length;
            const active = filter === card.value;
            return (
              <button
                key={card.value}
                onClick={() => setFilter(card.value)}
                className={`text-right p-4 rounded-xl shadow-md transition border-2 ${
                  active
                    ? `${card.border} ${card.ring} ${card.bg} ring-2`
                    : 'border-transparent bg-white hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-bold ${active ? card.text : 'text-gray-700'}`}>{card.label}</span>
                  <Icon size={20} className={active ? card.text : 'text-gray-400'} />
                </div>
                <span className="text-2xl font-bold text-gray-900">{count}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 opacity-30" />
            <p>هیچ مقاله‌ای برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredArticles.map(article => (
              <div key={article.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{article.title}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{article.abstract}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>نویسندگان: {article.articleAuthors?.length || 0}</span>
                      <span>{new Date(article.submissionDate).toLocaleDateString('fa-IR')}</span>
                      <span className={`px-3 py-1 rounded-full text-white text-xs font-semibold ${
                        (article.status || '').trim().toLowerCase() === 'published' ? 'bg-green-600' :
                        (article.status || '').trim().toLowerCase() === 'under review' ? 'bg-blue-600' :
                        (article.status || '').trim().toLowerCase() === 'draft' ? 'bg-yellow-600' :
                        (article.status || '').trim().toLowerCase() === 'rejected' ? 'bg-red-600' :
                        'bg-gray-600'
                      }`}>
                        {(article.status || '').trim().toLowerCase() === 'published' ? 'منتشر شده' :
                         (article.status || '').trim().toLowerCase() === 'under review' ? 'در حال بررسی' :
                         (article.status || '').trim().toLowerCase() === 'draft' ? 'پیش‌نویس' :
                         (article.status || '').trim().toLowerCase() === 'rejected' ? 'رد شده' :
                         (article.status || '').trim() || 'نامشخص'}
                      </span>
                      {article.isFeatured && (
                        <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold flex items-center gap-1">
                          <Star size={12} fill="currentColor" />
                          برگزیده
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap items-center pt-4 border-t border-gray-200">
                  <label className="text-sm font-semibold text-gray-700">تغییر وضعیت:</label>
                  <select
                    value={article.status || ''}
                    onChange={(e) => handleStatusChange(article.id, e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                  >
                    <option value="Draft">پیش‌نویس</option>
                    <option value="Under Review">در حال بررسی</option>
                    <option value="Published">منتشر شده</option>
                    <option value="Rejected">رد شده</option>
                  </select>

                  <button
                    onClick={() => handleToggleFeatured(article.id, !article.isFeatured)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm font-semibold ${
                      article.isFeatured
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    }`}
                  >
                    <Star size={16} fill={article.isFeatured ? 'currentColor' : 'none'} />
                    {article.isFeatured ? 'حذف از برگزیده' : 'افزودن به برگزیده'}
                  </button>

                  <button
                    onClick={() => handleDelete(article.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-semibold mr-auto"
                  >
                    <Trash2 size={16} />
                    حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
